import type { ReactNode } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { isApiFailure } from '../api/http';
import { CtaButton } from './Cta';

/** Плашка-предупреждение прототипа (`.alert`). Три тона: нейтральный, жёлтый, красный. */
export function Alert(props: {
  tone?: 'plain' | 'warn' | 'danger';
  title?: ReactNode;
  children?: ReactNode;
  action?: { label: string; onClick: () => void } | undefined;
}): JSX.Element {
  const tone = props.tone ?? 'plain';
  const box = {
    plain: 'bg-surface shadow-[inset_0_0_0_1px_var(--hair)]',
    warn: 'bg-[color-mix(in_srgb,var(--gold)_12%,var(--surface))] shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--gold)_35%,transparent)]',
    danger:
      'bg-[color-mix(in_srgb,var(--red)_12%,var(--surface))] shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--red)_35%,transparent)]',
  }[tone];
  const titleColor = { plain: 'text-ink', warn: 'text-gold', danger: 'text-danger' }[tone];

  return (
    <div className={`mb-2.5 rounded-card px-3.5 py-3.5 ${box}`}>
      {props.title ? (
        <div className={`mb-1.5 text-[14px] font-extrabold tracking-tight ${titleColor}`}>
          {props.title}
        </div>
      ) : null}
      {props.children ? (
        <div className="text-[12.5px] leading-[1.5] text-ink-2">{props.children}</div>
      ) : null}
      {props.action ? (
        <button
          type="button"
          onClick={props.action.onClick}
          className="mt-2 text-[12px] font-bold text-accent"
        >
          {props.action.label}
        </button>
      ) : null}
    </div>
  );
}

export function Loader(): JSX.Element {
  const { t } = useI18n();
  return <div className="px-4 py-8 text-center text-[13px] text-ink-3">{t('loading')}</div>;
}

export function EmptyState(props: { children: ReactNode }): JSX.Element {
  return <div className="px-1 py-3.5 text-[13px] leading-[1.5] text-ink-3">{props.children}</div>;
}

/**
 * Показ отказа.
 *
 * Любая ошибка проходит через словарь ключей (`describeError`). Ни одной ветки, которая
 * рисует «что-то пошло не так» без следа: неизвестный ключ выводится текстом вместе
 * с самим ключом, а неожиданное исключение — своим сообщением. Иначе пропавшая ветка
 * отказов на бэкенде выглядела бы как исправная работа (AGENTS.md, правило 7).
 */
/**
 * `withTitle={false}` — когда плашка стоит внутри шторки, чей заголовок уже говорит
 * «Не получилось». Два одинаковых заголовка подряд читаются как сбой вёрстки.
 */
export function ErrorState(props: {
  error: unknown;
  onRetry?: (() => void) | undefined;
  withTitle?: boolean;
}): JSX.Element {
  const { t, describeError } = useI18n();
  const described = isApiFailure(props.error)
    ? describeError({ key: props.error.key, details: props.error.details })
    : null;

  const text = described
    ? described.text
    : props.error instanceof Error
      ? props.error.message
      : String(props.error);

  return (
    // Внутри шторки собственные отступы не нужны: их уже даёт сама шторка, и вторые
    // отодвинули бы текст от заголовка на двойное расстояние.
    <div className={props.withTitle === false ? '' : 'px-4 py-6'}>
      <Alert tone="danger" title={props.withTitle === false ? undefined : t('errTitle')}>
        {text}
      </Alert>
      {props.onRetry ? (
        <CtaButton variant="ghost" onClick={props.onRetry}>
          {t('retry')}
        </CtaButton>
      ) : null}
    </div>
  );
}
